import type { DtoolsDisplaySettings } from "./dtoolsDisplaySettings";
import { filterVisibleDtoolsSystems } from "./dtoolsOptionVisibility";
import type { DtoolsReviewPrimary, DtoolsReviewSystem } from "./dtoolsStagingTransform";
import { productPrimaryBlocks } from "./dtoolsReviewNavigation";
import type { DtoolsReviewBuildContext } from "./dtoolsReviewNavigation";
import { resolveSystemDisplayLabel } from "./dtoolsReviewNavigation";

export type DtoolsCustomerProposalTabId = "clarity" | "summary" | `option-${number}` | "terms";

export type DtoolsViewSortMode = "option" | "rooms";

export type DtoolsViewByOptionGroup = {
  optionIndex: number;
  optionLabel: string;
  system: DtoolsReviewSystem;
  rooms: Array<{ name: string; primaries: DtoolsReviewPrimary[] }>;
  systemLevelPrimaries: DtoolsReviewPrimary[];
};

export type DtoolsViewByRoomGroup = {
  roomName: string;
  options: Array<{
    optionIndex: number;
    optionLabel: string;
    primaries: DtoolsReviewPrimary[];
  }>;
};

function normalizeRoom(value: string | null | undefined): string {
  return String(value ?? "").trim() || "Unassigned";
}

export function buildDtoolsCustomerTabItems(
  systems: DtoolsReviewSystem[],
  reviewContext?: DtoolsReviewBuildContext,
  displaySettings?: DtoolsDisplaySettings,
): Array<{ id: DtoolsCustomerProposalTabId; label: string }> {
  const items: Array<{ id: DtoolsCustomerProposalTabId; label: string }> = [];
  if (!displaySettings?.hideClarityIntroduction) {
    items.push({ id: "clarity", label: "Clarity" });
  }
  const visibleSystems = filterVisibleDtoolsSystems(systems, displaySettings);
  if (visibleSystems.length > 0) {
    items.push({ id: "summary", label: "System Summary" });
  }
  for (const { system, optionIndex } of visibleSystems) {
    items.push({
      id: `option-${optionIndex}`,
      label: resolveSystemDisplayLabel(system, reviewContext, optionIndex),
    });
  }
  items.push({ id: "terms", label: "Terms and Conditions" });
  return items;
}

/** Room-grouped equipment for a single option (Sort by Room). */
export function buildDtoolsOptionRoomGroups(
  system: DtoolsReviewSystem,
): Array<{ name: string; primaries: DtoolsReviewPrimary[] }> {
  const rooms = system.rooms.map((room) => ({
    name: room.name?.trim() || "Unassigned",
    primaries: productPrimaryBlocks(room.primaries),
  }));
  if (system.systemLevelPrimaries.length > 0) {
    rooms.push({
      name: "System level",
      primaries: productPrimaryBlocks(system.systemLevelPrimaries),
    });
  }
  return rooms.filter((room) => room.primaries.length > 0);
}

export function buildDtoolsViewByOption(
  systems: DtoolsReviewSystem[],
  reviewContext?: DtoolsReviewBuildContext,
  displaySettings?: DtoolsDisplaySettings,
): DtoolsViewByOptionGroup[] {
  return filterVisibleDtoolsSystems(systems, displaySettings).map(({ system, optionIndex }) => ({
    optionIndex,
    optionLabel: resolveSystemDisplayLabel(system, reviewContext, optionIndex),
    system,
    rooms: system.rooms.map((room) => ({
      name: room.name,
      primaries: productPrimaryBlocks(room.primaries),
    })),
    systemLevelPrimaries: productPrimaryBlocks(system.systemLevelPrimaries),
  }));
}

export function buildDtoolsViewByRooms(
  systems: DtoolsReviewSystem[],
  reviewContext?: DtoolsReviewBuildContext,
  displaySettings?: DtoolsDisplaySettings,
): DtoolsViewByRoomGroup[] {
  const roomMap = new Map<string, DtoolsViewByRoomGroup>();

  for (const { system, optionIndex } of filterVisibleDtoolsSystems(systems, displaySettings)) {
    const optionLabel = resolveSystemDisplayLabel(system, reviewContext, optionIndex);
    for (const room of system.rooms) {
      const roomName = normalizeRoom(room.name);
      let group = roomMap.get(roomName);
      if (!group) {
        group = { roomName, options: [] };
        roomMap.set(roomName, group);
      }
      group.options.push({
        optionIndex,
        optionLabel,
        primaries: productPrimaryBlocks(room.primaries),
      });
    }
    if (system.systemLevelPrimaries.length > 0) {
      const roomName = "System level";
      let group = roomMap.get(roomName);
      if (!group) {
        group = { roomName, options: [] };
        roomMap.set(roomName, group);
      }
      group.options.push({
        optionIndex,
        optionLabel,
        primaries: productPrimaryBlocks(system.systemLevelPrimaries),
      });
    }
  }

  // Preserve first-appearance order from visible systems/rooms (canonical sortOrder hierarchy).
  return [...roomMap.values()];
}

export function buildDtoolsOverviewOptionComparison(
  systems: DtoolsReviewSystem[],
  reviewContext?: DtoolsReviewBuildContext,
  displaySettings?: DtoolsDisplaySettings,
): string[] {
  const visible = filterVisibleDtoolsSystems(systems, displaySettings);
  if (visible.length < 2) return [];
  return visible.map(({ system, optionIndex }) => {
    const label = resolveSystemDisplayLabel(system, reviewContext, optionIndex);
    return `${label} — a distinct equipment package tailored to different performance and budget goals.`;
  });
}
